<template>
  <main class="special-page">
    <div class="special-shell">
      <header class="special-header">
        <div>
          <div class="header-kicker">{{ $t('specialContents.creatorStudio') }}</div>
          <h1>{{ $t('specialContents.title') }}</h1>
          <p>{{ $t('specialContents.headerDescription') }}</p>
        </div>
      </header>

      <section class="upload-panel">
        <div class="section-heading">
          <div>
            <span class="section-kicker">{{ $t('specialContents.newContent') }}</span>
            <h2>{{ $t('specialContents.addPost') }}</h2>
          </div>
          <span class="upload-mark"><feather-icon icon="UploadCloudIcon" size="19" /></span>
        </div>

        <button class="dropzone" type="button" @click="$refs.fileInput.click()">
          <template v-if="selectedPreview">
            <video v-if="selectedIsVideo" :src="selectedPreview" class="selected-preview" controls @click.stop />
            <img v-else :src="selectedPreview" class="selected-preview" alt="Seçilen içerik önizlemesi" />
            <span class="change-file">Dosyayı değiştir</span>
          </template>
          <template v-else>
            <span class="dropzone-icon"><feather-icon icon="PlusIcon" size="24" /></span>
            <strong>{{ $t('specialContents.chooseMedia') }}</strong>
            <span>{{ $t('specialContents.videoLimit') }}</span>
          </template>
        </button>
        <input
          ref="fileInput"
          class="file-input"
          type="file"
          accept="image/*,video/*"
          @change="onFileSelected"
        />

        <label class="caption-label" for="special-caption">{{ $t('specialContents.caption') }} <span>{{ $t('specialContents.optional') }}</span></label>
        <textarea
          id="special-caption"
          v-model.trim="caption"
          class="caption-input"
          maxlength="100"
          :placeholder="$t('specialContents.captionPlaceholder')"
          rows="3"
        />
        <div class="caption-count">{{ caption.length }}/100</div>

        <button class="publish-button" type="button" :disabled="!selectedFile || submitting" @click="submitContent">
          <span v-if="submitting" class="button-spinner" />
          <feather-icon v-else icon="UploadIcon" size="17" />
          {{ submitting ? $t('specialContents.uploading') : $t('specialContents.upload') }}
        </button>
      </section>

      <section class="library-section">
        <div class="library-heading">
          <div>
            <span class="section-kicker">{{ $t('specialContents.library') }}</span>
            <h2>{{ $t('specialContents.posts') }}</h2>
          </div>
          <span class="item-count">{{ items.length }}</span>
        </div>

        <div v-if="loading" class="state-card">
          <span class="button-spinner button-spinner--dark" />
          <span>{{ $t('specialContents.loadingContent') }}</span>
        </div>
        <div v-else-if="items.length === 0" class="empty-state">
          <span class="empty-icon"><feather-icon icon="ImageIcon" size="25" /></span>
          <strong>{{ $t('specialContents.emptyTitle') }}</strong>
          <span>{{ $t('specialContents.emptyDescription') }}</span>
        </div>
        <div v-else class="content-grid">
          <article v-for="item in items" :key="item.id" class="content-card">
            <div class="content-media">
              <video v-if="item.mediaType === 'video'" :src="item.mediaUrl" controls preload="metadata" />
              <img v-else :src="item.mediaUrl" :alt="item.content || $t('specialContents.title')" loading="lazy" />
              <span class="status-pill" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
            </div>
            <div class="content-info">
              <p v-if="item.content" class="content-caption">{{ item.content }}</p>
              <time>{{ formatDate(item.createdAt) }}</time>
              <button class="delete-button" type="button" :disabled="deletingId === item.id" @click="deleteContent(item)">
                <feather-icon :icon="deletingId === item.id ? 'LoaderIcon' : 'Trash2Icon'" size="15" />
                {{ $t('specialContents.delete') }}
              </button>
            </div>
          </article>
        </div>

        <button v-if="hasMore && !loading" class="load-more" type="button" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? $t('specialContents.loading') : $t('specialContents.showMore') }}
        </button>
      </section>
    </div>
  </main>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import { throwDefaultError } from '@/helpers/helpers'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const PAGE_SIZE = 20

export default {
  name: 'SpecialContentsManager',
  data() {
    return {
      items: [],
      selectedFile: null,
      selectedPreview: '',
      selectedIsVideo: false,
      caption: '',
      loading: true,
      loadingMore: false,
      submitting: false,
      deletingId: null,
      hasMore: false,
    }
  },
  created() {
    this.fetchItems(0, false)
  },
  beforeDestroy() {
    if (this.selectedPreview) URL.revokeObjectURL(this.selectedPreview)
  },
  methods: {
    fetchItems(offset, append) {
      useJwt.specialContents(offset, PAGE_SIZE)
        .then(response => {
          const body = response && response.data ? response.data.body : []
          const rows = Array.isArray(body) ? body : []
          this.items = append ? this.items.concat(rows) : rows
          this.hasMore = rows.length === PAGE_SIZE
        })
        .catch(error => throwDefaultError(this.$toast, error))
        .finally(() => {
          this.loading = false
          this.loadingMore = false
        })
    },
    loadMore() {
      if (this.loadingMore) return
      this.loadingMore = true
      this.fetchItems(this.items.length, true)
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('specialContents.invalidFile'), icon: 'AlertTriangleIcon', variant: 'danger', text: this.$t('specialContents.chooseValidFile') },
        })
        event.target.value = ''
        return
      }
      if (this.selectedPreview) URL.revokeObjectURL(this.selectedPreview)
      this.selectedFile = file
      this.selectedIsVideo = file.type.startsWith('video/')
      this.selectedPreview = URL.createObjectURL(file)
    },
    submitContent() {
      if (!this.selectedFile || this.submitting) return
      const form = new FormData()
      form.append('file', this.selectedFile)
      if (this.caption) form.append('content', this.caption)

      this.submitting = true
      useJwt.specialContentCreate(form)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('specialContents.submitted'), icon: 'CheckIcon', variant: 'success', text: this.$t('specialContents.submittedText') },
          })
          this.resetForm()
          this.loading = true
          this.fetchItems(0, false)
        })
        .catch(error => throwDefaultError(this.$toast, error))
        .finally(() => { this.submitting = false })
    },
    resetForm() {
      if (this.selectedPreview) URL.revokeObjectURL(this.selectedPreview)
      this.selectedFile = null
      this.selectedPreview = ''
      this.selectedIsVideo = false
      this.caption = ''
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    deleteContent(item) {
      if (!window.confirm(this.$t('specialContents.confirmDelete'))) return
      this.deletingId = item.id
      useJwt.specialContentDelete(item.id)
        .then(() => {
          this.items = this.items.filter(row => row.id !== item.id)
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('specialContents.deleted'), icon: 'CheckIcon', variant: 'success', text: this.$t('specialContents.deletedText') },
          })
        })
        .catch(error => throwDefaultError(this.$toast, error))
        .finally(() => { this.deletingId = null })
    },
    statusLabel(status) {
      if (Number(status) === 1) return this.$t('specialContents.published')
      if (Number(status) === 2) return this.$t('specialContents.rejected')
      return this.$t('specialContents.underReview')
    },
    statusClass(status) {
      if (Number(status) === 1) return 'status-pill--live'
      if (Number(status) === 2) return 'status-pill--rejected'
      return 'status-pill--pending'
    },
    formatDate(value) {
      if (!value) return ''
      const locale = this.$i18n.locale === 'ua' ? 'uk' : (this.$i18n.locale || 'tr')
      return new Intl.DateTimeFormat(locale, {
        day: 'numeric', month: 'short', year: 'numeric',
      }).format(new Date(value))
    },
  },
}
</script>

<style lang="scss" scoped>
.special-page {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  padding: 22px 18px 24px;
  color: #f8f6fb;
  border-radius: 22px;
  background: radial-gradient(ellipse at 15% 0%, rgba(113, 58, 151, .24), transparent 38%), #100d16;
}

.special-shell { box-sizing: border-box; width: min(100%, 980px); margin: 0 auto; }
.special-header { display: flex; align-items: flex-start; gap: 16px; margin: 0 auto 28px; }
.header-kicker, .section-kicker { color: #d8a8ef; font-size: 10px; font-weight: 800; letter-spacing: .16em; }
.special-header h1 { margin: 4px 0 5px; color: #fff; font-size: clamp(25px, 5vw, 34px); font-weight: 750; }
.special-header p { margin: 0; color: #aaa2b4; font-size: 14px; }
.upload-panel, .library-section { padding: 24px; border: 1px solid rgba(255,255,255,.09); border-radius: 22px; background: linear-gradient(145deg, rgba(37,29,47,.96), rgba(25,21,32,.98)); box-shadow: 0 18px 48px rgba(0,0,0,.2); }
.upload-panel { margin-bottom: 26px; }
.section-heading, .library-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.section-heading h2, .library-heading h2 { margin: 4px 0 0; color: #fff; font-size: 19px; font-weight: 700; }
.upload-mark, .empty-icon { display: grid; width: 42px; height: 42px; place-items: center; color: #e9b2fb; border-radius: 13px; background: rgba(198,104,225,.14); }
.dropzone { display: flex; width: 100%; min-height: 184px; align-items: center; justify-content: center; flex-direction: column; gap: 8px; overflow: hidden; color: #fff; border: 1px dashed rgba(213,164,231,.45); border-radius: 17px; background: rgba(12,10,17,.42); }
.dropzone strong { font-size: 14px; }
.dropzone > span:last-child { color: #9c93a7; font-size: 12px; }
.dropzone-icon { display: grid; width: 44px; height: 44px; place-items: center; color: #ecc4fb; border-radius: 50%; background: rgba(187,90,220,.2); }
.selected-preview { display: block; width: 100%; max-height: 280px; object-fit: contain; background: #08070b; }
.change-file { padding: 4px 0; color: #e4b7f3; font-size: 12px; }
.file-input { display: none; }
.caption-label { display: block; margin: 18px 0 8px; color: #f3eef7; font-size: 13px; font-weight: 650; }
.caption-label span { margin-left: 5px; color: #92899b; font-weight: 400; }
.caption-input { display: block; width: 100%; resize: vertical; padding: 12px 14px; color: #fff; border: 1px solid rgba(255,255,255,.12); border-radius: 13px; outline: none; background: rgba(9,8,12,.45); }
.caption-input:focus { border-color: rgba(207,129,231,.7); }
.caption-input::placeholder { color: #777080; }
.caption-count { margin-top: 5px; color: #837b8c; font-size: 11px; text-align: right; }
.publish-button { display: flex; width: 100%; min-height: 48px; align-items: center; justify-content: center; gap: 9px; margin-top: 14px; color: #fff; border: 0; border-radius: 14px; background: linear-gradient(100deg, #a855d5, #7446cd); font-weight: 700; box-shadow: 0 8px 22px rgba(146,69,194,.2); }
.publish-button:disabled { opacity: .48; cursor: not-allowed; }
.library-heading { margin-bottom: 16px; }
.item-count { display: grid; min-width: 32px; height: 32px; padding: 0 9px; place-items: center; color: #e7c0f4; border-radius: 11px; background: rgba(195,110,222,.16); font-size: 13px; font-weight: 700; }
.content-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.content-card { min-width: 0; overflow: hidden; border: 1px solid rgba(255,255,255,.08); border-radius: 16px; background: rgba(10,9,14,.45); }
.content-media { position: relative; aspect-ratio: 4 / 5; overflow: hidden; background: #09080c; }
.content-media img, .content-media video { width: 100%; height: 100%; object-fit: cover; }
.status-pill { position: absolute; top: 9px; left: 9px; padding: 5px 9px; border: 1px solid rgba(255,255,255,.16); border-radius: 999px; color: #fff; background: rgba(18,15,23,.78); font-size: 10px; font-weight: 700; backdrop-filter: blur(8px); }
.status-pill--live { color: #b6f3cb; }
.status-pill--pending { color: #ffe4a3; }
.status-pill--rejected { color: #ffb3ba; }
.content-info { position: relative; min-height: 68px; padding: 11px 12px 35px; }
.content-caption { overflow: hidden; margin: 0 0 5px; color: #eee8f2; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.content-info time { color: #918999; font-size: 11px; }
.delete-button { position: absolute; right: 10px; bottom: 8px; display: inline-flex; align-items: center; gap: 5px; padding: 4px 7px; color: #c5bacb; border: 0; border-radius: 8px; background: rgba(255,255,255,.06); font-size: 11px; }
.delete-button:disabled { opacity: .5; }
.empty-state, .state-card { display: flex; min-height: 172px; align-items: center; justify-content: center; flex-direction: column; gap: 9px; color: #a39aaa; text-align: center; }
.empty-state strong { color: #f2edf5; font-size: 14px; }
.empty-state > span:last-child { max-width: 290px; font-size: 12px; line-height: 1.5; }
.empty-icon { width: 50px; height: 50px; margin-bottom: 3px; }
.load-more { display: block; margin: 20px auto 0; padding: 10px 17px; color: #e8c1f5; border: 1px solid rgba(216,168,239,.25); border-radius: 12px; background: rgba(255,255,255,.04); font-weight: 600; }
.button-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; }
.button-spinner--dark { border-color: rgba(187,90,220,.25); border-top-color: #be77d9; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 700px) {
  .special-page { padding: 17px 12px 20px; }
  .special-header { gap: 12px; margin-bottom: 21px; }
  .upload-panel, .library-section { padding: 16px; border-radius: 19px; }
  .content-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .content-info { padding-right: 8px; padding-left: 9px; }
}

@media (max-width: 360px) {
  .special-page { padding-right: 10px; padding-left: 10px; }
  .content-grid { grid-template-columns: 1fr; }
}
</style>
